/**
 * Scheduling API contracts (Phase 9A). Mirrors blackbird_backend
 * scheduling/scheduleService.js formatShift / getTodayBoard / getCoverageWeek.
 *
 * A SERIES is a template; every occurrence is a real SHIFT with its own HHAX scheduleRef.
 * Dates are agency-local 'YYYY-MM-DD'; startAt/endAt are UTC ISO instants.
 */
import { ActiveDisciplineCode, disciplineCodes, getDisciplineColors, providerTypeEntries } from '../../constants/disciplines';

/** Stored shift status (uppercase). Late / Unstaffed / Missed are derived flags, never stored. */
export type ShiftStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

/** @deprecated pre-9A lowercase status; kept for older call sites. */
export type ScheduleStatus = 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';

export type ShiftVisitType = 'Initial' | 'Routine' | 'Discharge' | 'Evaluation' | 'Skilled';
export const SHIFT_VISIT_TYPES: ShiftVisitType[] = ['Routine', 'Skilled', 'Initial', 'Evaluation', 'Discharge'];

/** Shift discipline: a registry discipline, or a limited nursing provider type (LVN) a shift can ask for. */
export type Discipline = ActiveDisciplineCode | 'LVN';
/** Phase 20A: registry disciplines, with LVN after RN (mirrors the backend scheduleService list) */
export const DISCIPLINES: Discipline[] = disciplineCodes().flatMap(code => [
  code,
  ...providerTypeEntries().filter(p => p.discipline === code && p.limitedNursing).map(p => p.code),
]) as Discipline[];

export type CancelCode = 'HOSPITALIZED' | 'FAMILY' | 'STAFF' | 'WEATHER' | 'OTHER' | 'DISCHARGED';
export const CANCEL_REASONS: { code: CancelCode; label: string }[] = [
  { code: 'HOSPITALIZED', label: 'Patient hospitalized' },
  { code: 'FAMILY', label: 'Family request' },
  { code: 'STAFF', label: 'Staff unavailable' },
  { code: 'WEATHER', label: 'Weather' },
  { code: 'DISCHARGED', label: 'Discharged' },
  { code: 'OTHER', label: 'Other (note required)' },
];

export type ShiftPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type EditScope = 'this' | 'following' | 'all';

export interface ShiftPerson {
  id: string;
  name: string;
}

export interface ShiftProvider extends ShiftPerson {
  firstName?: string;
  discipline: string;
  credential?: string;
  clinicianCode?: string | null;
}

export interface ShiftPatient extends ShiftPerson {
  mrn: string;
  address: string;
}

export interface ShiftFlags {
  late: boolean;
  unstaffed: boolean;
  missed: boolean;
}

export interface Shift {
  id: string;
  kind: 'SHIFT';
  seriesId: string | null;
  isRecurring: boolean;
  detached: boolean;
  scheduleRef: string | null;
  title: string;
  visitType: ShiftVisitType;
  discipline: string;
  patientId: string;
  providerId: string | null;
  patient: ShiftPatient;
  provider: ShiftProvider | null;
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  startAt: string | null;
  endAt: string | null;
  overnight: boolean;
  durationMinutes: number | null;
  status: ShiftStatus;
  cancel: { code: CancelCode; note: string | null; by: string; at: string } | null;
  visitId: string | null;
  authId: string | null;
  notes: string;
  priority: ShiftPriority;
  /** patients.status: 'active' | 'on_hold' | 'pending_admission' | 'inactive' */
  patientStatus: string | null;
  flags: ShiftFlags;
  createdAt?: string;
  createdBy?: string | null;
  updatedAt?: string;
}

export interface RecurrenceRule {
  freq: 'daily' | 'weekly' | 'monthly';
  interval?: number;
  /** 0 = Sunday … 6 = Saturday */
  byDays?: number[];
  until?: string | null;
  count?: number | null;
}

export interface Series {
  id: string;
  status: 'ACTIVE' | 'ENDED';
  patientId: string;
  rule: RecurrenceRule & { startDate: string };
  template: Record<string, unknown>;
  horizonThrough: string;
}

export interface CreateShiftRequest {
  patientId: string;
  providerId: string | null;
  discipline?: Discipline;
  visitType: ShiftVisitType;
  date: string;
  startTime: string;
  endTime: string;
  recurrence?: RecurrenceRule | null;
  authId?: string | null;
  notes?: string;
  priority?: ShiftPriority;
  title?: string;
  acknowledgeWarnings?: boolean;
  addToCareTeam?: boolean;
}

export type UpdateShiftRequest = Partial<Pick<CreateShiftRequest,
  'providerId' | 'discipline' | 'visitType' | 'date' | 'startTime' | 'endTime' | 'authId' | 'notes' | 'priority' | 'title'>> & {
  acknowledgeWarnings?: boolean;
  addToCareTeam?: boolean;
};

export type WarningCode =
  | 'SOC_INCOMPLETE' | 'PROVIDER_OVERLAP' | 'PATIENT_OVERLAP' | 'LICENSE_EXPIRED' | 'LICENSE_EXPIRING'
  | 'DISCIPLINE_MISMATCH' | 'NOT_ON_CARE_TEAM' | 'NO_PA_COVERS_DATE' | 'PA_OVERSCHEDULED'
  | 'PA_WEEKLY_EXCEEDED' | 'BEYOND_PA_END'
  /** Phase 20C: a PTA / COTA / SLPA with no licensed therapist of the discipline on the care team */
  | 'ASSISTANT_SUPERVISOR_MISSING'
  /** Phase 20D: skilled nursing and aide visits */
  | 'VISIT_OVER_PER_VISIT_LIMIT' | 'SN_VISITS_PER_DAY' | 'PDN_SN_SAME_DAY'
  /** Phase 20F: patient programmes */
  | 'HOSPICE_ELECTED' | 'PPECC_ATTENDANCE_DAY';

export interface ScheduleWarning {
  code: WarningCode;
  severity: 'warning' | 'info';
  message: string;
  dates?: string[];
  count?: number;
  missing?: string[];
  canAddToCareTeam?: boolean;
  patientId?: string;
  [key: string]: unknown;
}

export interface SchedulePreview {
  count: number;
  firstDate: string | null;
  lastDate: string | null;
  dates: string[];
  totalMinutes: number;
  hours: number;
  paUnits: { authorizationId: string; units: number; unitType: string; approved: number; used: number }[];
  horizonThrough?: string | null;
}

export interface DryRunResult {
  dryRun: true;
  warnings: ScheduleWarning[];
  preview: SchedulePreview;
  targetCount?: number;
}

export interface ShiftWriteResult {
  shifts: Shift[];
  series?: Series | null;
  warnings: ScheduleWarning[];
}

export interface ShiftListResult {
  items: Shift[];
  count: number;
}

export type BoardColumn = 'LATE' | 'UNSTAFFED' | 'UPCOMING' | 'IN_PROGRESS' | 'DONE';

/** VIS-07: a visit in progress 16 hours or more (the coordinator closes it). */
export interface AbandonedVisit {
  visitId: string;
  patientId: string;
  patientName: string;
  providerId: string;
  providerName: string;
  startedAt: string | null;
  hours: number;
  scheduleId: string | null;
  scheduledEnd: string | null;
}

/** Phase 16C (16B B9): offline-synced visits flagged for coordinator review (ids and flags). */
export type OfflineReviewFlag = 'PATIENT_ON_HOLD' | 'OVERLAP' | 'OVERLAP_SELF' | 'SHIFT_UNMATCHED' | 'PA_NOT_COVERED' | 'TIME_UNVERIFIED' | string;

export interface OfflineReviewItem {
  visitId: string;
  patientId: string;
  flags: OfflineReviewFlag[];
  patientName?: string | null;
  providerName?: string | null;
  startedAt?: string | null;
}

export interface TodayBoard {
  date: string;
  timezone: string;
  now: string;
  abandoned?: AbandonedVisit[];
  offlineReview?: OfflineReviewItem[];
  columns: Record<BoardColumn, Shift[]>;
  counts: Record<BoardColumn, number>;
}

export interface CoverageDay {
  date: string;
  shifts: Shift[];
  scheduledMinutes: number;
}

export interface CoverageRow {
  patientId: string;
  patientName: string;
  patientStatus: string | null;
  days: CoverageDay[];
  scheduledMinutes: number;
  cancelledMinutes: number;
  unstaffedCount: number;
  approvedMinutesPerWeek: number | null;
  pas: { authorizationId: string; serviceCategory: string; approvedPerWeek: number | null; unitType: string | null; unitsLeft: number; approvedEndDate: string }[];
}

export interface CoverageWeek {
  weekStart: string;
  weekEnd: string;
  days: string[];
  rows: CoverageRow[];
}

export type BulkAction = 'cancel' | 'reassign' | 'copyWeek' | 'cancelPatientFuture';

export interface BulkResult {
  dryRun?: boolean;
  cancelled?: number;
  updated?: number;
  created?: number;
  count?: number;
  seriesEnded?: number;
  skipped?: { id: string; code: string }[];
  conflicts?: { sourceId: string; date: string; patientId: string; code: string }[];
  warnings?: ScheduleWarning[];
}

export interface ShiftFilters {
  startDate: string;
  endDate: string;
  providerId?: string;
  patientId?: string;
  discipline?: string;
  status?: string;
  visitType?: string;
  unstaffed?: boolean;
}

/** Colors: discipline is the left border; status is a pill. One source for both. */
export const STATUS_STYLES: Record<ShiftStatus, { label: string; pill: string }> = {
  SCHEDULED: { label: 'Scheduled', pill: 'bg-blue-100 text-blue-800' },
  IN_PROGRESS: { label: 'In progress', pill: 'bg-indigo-100 text-indigo-800' },
  COMPLETED: { label: 'Completed', pill: 'bg-slate-100 text-slate-700' },
  CANCELLED: { label: 'Cancelled', pill: 'bg-red-100 text-red-700' },
  NO_SHOW: { label: 'No show', pill: 'bg-orange-100 text-orange-800' },
};

/** Phase 20A (D-12): left border per discipline from the registry palette; LVN a softer nursing border. */
export const DISCIPLINE_BORDER: Record<string, string> = Object.fromEntries([
  ...disciplineCodes().map(code => [code, getDisciplineColors(code).accentBorder]),
  ...providerTypeEntries().filter(p => p.limitedNursing).map(p => [p.code, getDisciplineColors(p.discipline).accentBorderSoft]),
]);
